"use client";

import {
  WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY,
  WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS,
  type WebappSignupBeforeDemoExposure,
  type WebappSignupBeforeDemoVariant,
  parseWebappSignupBeforeDemoVariant,
  recordWebappSignupBeforeDemoExposure,
} from "@/lib/experiments/webappSignupBeforeDemo";
import { useEffect, useState } from "react";

type FeatureFlagLoadContext = { errorsLoading?: boolean };

export type WebappSignupBeforeDemoPostHogClient = {
  getFeatureFlagResult: (
    key: string,
    options?: { fresh?: boolean; send_event?: boolean }
  ) => { variant?: string } | undefined;
  onFeatureFlags: (
    callback: (
      flags: string[],
      variants: Record<string, string | boolean>,
      context?: FeatureFlagLoadContext
    ) => void
  ) => () => void;
  reloadFeatureFlags: () => void;
};

export type WebappSignupBeforeDemoExperimentResolution = {
  status: "loading" | "resolved";
  variant: WebappSignupBeforeDemoVariant;
  source: "pending" | "posthog" | "fallback" | "bypassed";
  reason?: "disabled" | "invalid_variant" | "load_error" | "timeout" | "unavailable";
};

const LOADING: WebappSignupBeforeDemoExperimentResolution = {
  status: "loading",
  variant: WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.control,
  source: "pending",
};

function fallback(
  reason: NonNullable<WebappSignupBeforeDemoExperimentResolution["reason"]>,
  source: "fallback" | "bypassed" = "fallback"
): WebappSignupBeforeDemoExperimentResolution {
  return {
    status: "resolved",
    variant: WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.control,
    source,
    reason,
  };
}

export function useWebappSignupBeforeDemoExperiment({
  enabled,
  posthog,
  timeoutMs = 1_500,
}: {
  enabled: boolean;
  posthog: WebappSignupBeforeDemoPostHogClient | null | undefined;
  timeoutMs?: number;
}): WebappSignupBeforeDemoExperimentResolution {
  const [resolution, setResolution] = useState<WebappSignupBeforeDemoExperimentResolution>(() =>
    enabled ? LOADING : fallback("disabled", "bypassed")
  );

  useEffect(() => {
    if (!enabled) {
      setResolution(fallback("disabled", "bypassed"));
      return;
    }
    if (!posthog) {
      setResolution(fallback("unavailable"));
      return;
    }

    let settled = false;
    let reloadRequested = false;
    let unsubscribe: () => void = () => undefined;
    setResolution(LOADING);

    const finish = (next: WebappSignupBeforeDemoExperimentResolution) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      setResolution(next);
    };
    const timeoutId = window.setTimeout(() => finish(fallback("timeout")), timeoutMs);

    try {
      unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
        if (settled || !reloadRequested) return;
        if (context?.errorsLoading) {
          finish(fallback("load_error"));
          return;
        }

        try {
          const result = posthog.getFeatureFlagResult(WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY, {
            fresh: true,
            send_event: false,
          });
          const variant = parseWebappSignupBeforeDemoVariant(result?.variant);
          if (!variant) {
            finish(fallback("invalid_variant"));
            return;
          }
          finish({ status: "resolved", variant, source: "posthog" });
        } catch {
          finish(fallback("unavailable"));
        }
      });
      reloadRequested = true;
      posthog.reloadFeatureFlags();
    } catch {
      finish(fallback("unavailable"));
    }

    return () => {
      settled = true;
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [enabled, posthog, timeoutMs]);

  return resolution;
}

export function exposeWebappSignupBeforeDemoExperiment(
  posthog: WebappSignupBeforeDemoPostHogClient,
  renderedVariant: WebappSignupBeforeDemoVariant
): WebappSignupBeforeDemoExposure | null {
  try {
    const current = posthog.getFeatureFlagResult(WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY, {
      fresh: true,
      send_event: false,
    });
    if (parseWebappSignupBeforeDemoVariant(current?.variant) !== renderedVariant) return null;

    posthog.getFeatureFlagResult(WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY, {
      fresh: true,
      send_event: true,
    });
    return recordWebappSignupBeforeDemoExposure({ variant: renderedVariant });
  } catch {
    return null;
  }
}
